/**
 * Every setting in docs/spec/07 §5 as a row: how it shows, how left/right change it, and one line
 * of explanation. Presentation rows settle the preset (Custom unless they still match one). Pure.
 */

import type { Options } from '../../platform/options';
import { settlePreset, withPreset } from '../../platform/options';
import type { RowValue } from '../kit/optionRow';
import { wrapIndex } from '../kit/menuModel';

export interface OptionRow {
  readonly id: string;
  readonly group: string;
  readonly label: string;
  /** One line, at most 38 characters. */
  readonly help: string;
  value(o: Options): RowValue;
  /** Left (-1) or right (+1); absent for a row that opens another screen. */
  step?(o: Options, delta: number): Options;
  /** Set a level row straight to a value (clicking its bar). */
  set?(o: Options, value: number): Options;
}

type Key = Exclude<keyof Options, 'keys'>;

function choice<K extends Key>(key: K, values: readonly Options[K][], labels: readonly string[], presentation = false) {
  return {
    value: (o: Options): RowValue => ({ kind: 'choice', text: labels[values.indexOf(o[key])] ?? '?' }),
    step: (o: Options, delta: number): Options => {
      const next = values[wrapIndex(values.indexOf(o[key]) + delta, values.length)] ?? o[key];
      const changed = { ...o, [key]: next } as Options;
      return presentation ? settlePreset(changed) : changed;
    },
  };
}

function onOff<K extends Key>(key: K, presentation = false) {
  return choice(key, [true, false] as unknown as readonly Options[K][], ['ON', 'OFF'], presentation);
}

function offOn<K extends Key>(key: K) {
  return choice(key, [false, true] as unknown as readonly Options[K][], ['OFF', 'ON']);
}

function level(key: 'master' | 'sfx' | 'music') {
  const clamp = (v: number): number => Math.max(0, Math.min(10, Math.round(v)));
  return {
    value: (o: Options): RowValue => ({ kind: 'level', value: o[key], max: 10 }),
    step: (o: Options, delta: number): Options => ({ ...o, [key]: clamp(o[key] + delta) }),
    set: (o: Options, v: number): Options => ({ ...o, [key]: clamp(v) }),
  };
}

const PRESET_NAMES = { enhanced: 'ENHANCED', remastered: 'REMASTERED', authentic: 'C64 AUTHENTIC', custom: 'CUSTOM' } as const;

const P = 'PRESENTATION';
const A = 'AUDIO';
const G = 'GAMEPLAY';
const X = 'ACCESSIBILITY';
const C = 'CONTROLS';

export const OPTION_ROWS: readonly OptionRow[] = [
  {
    id: 'preset', group: P, label: 'PRESET', help: 'C64 AS IT WAS, ENHANCED OR REMASTERED.',
    value: (o) => ({ kind: 'choice', text: PRESET_NAMES[o.preset] }),
    step: (o, d) => {
      const order = ['enhanced', 'remastered', 'authentic', 'custom'] as const;
      return withPreset(o, order[wrapIndex(order.indexOf(o.preset) + d, order.length)] ?? 'enhanced');
    },
  },
  {
    id: 'art', group: P, label: 'ART', help: 'C64 PIXELS OR SHADED REMASTERED ART.',
    ...choice('art', ['c64', 'remastered'], ['C64', 'REMASTERED'], true),
  },
  {
    id: 'lighting', group: P, label: 'LIGHTING', help: 'LAMPLIGHT AND GLOWS (REMASTERED ART).',
    ...onOff('lighting', true),
  },
  {
    id: 'smooth', group: P, label: 'SMOOTH MOTION', help: 'SLIDE MOVING THINGS FROM CELL TO CELL.',
    ...onOff('smooth', true),
  },
  { id: 'particles', group: P, label: 'PARTICLES', help: 'CRUMBS, DUST AND SPARKLES.', ...onOff('particles', true) },
  {
    id: 'shake', group: P, label: 'SCREEN SHAKE', help: 'A LITTLE SHAKE WHEN THINGS EXPLODE.',
    ...onOff('shake', true),
  },
  { id: 'wide', group: P, label: 'WIDE VIEW', help: 'SEE MORE OF THE CAVE ON WIDE SCREENS.', ...onOff('wide', true) },
  {
    id: 'crt', group: P, label: 'DISPLAY', help: 'TV LOOK: SOFT GLOW, ARCADE CRT OR OFF.',
    ...choice('crt', ['soft', 'arcade', 'off'], ['SOFT', 'ARCADE', 'OFF'], true),
  },
  {
    id: 'scale', group: P, label: 'SCALING', help: 'CRISP WHOLE PIXELS OR FILL THE WINDOW.',
    ...choice('scale', ['pixel', 'fit'], ['PIXEL-PERFECT', 'FIT']),
  },
  { id: 'master', group: A, label: 'MASTER', help: 'OVERALL VOLUME.', ...level('master') },
  { id: 'sfx', group: A, label: 'EFFECTS', help: 'SOUND EFFECTS VOLUME.', ...level('sfx') },
  { id: 'music', group: A, label: 'MUSIC', help: 'MUSIC VOLUME.', ...level('music') },
  {
    id: 'caveMusic', group: A, label: 'IN-CAVE MUSIC', help: 'KEEP THE MUSIC PLAYING IN THE CAVES.',
    ...offOn('caveMusic'),
  },
  {
    id: 'speed', group: G, label: 'SPEED', help: 'RELAXED: 25% SLOWER; SCORES MARKED R.',
    ...choice('speed', ['normal', 'relaxed'], ['NORMAL', 'RELAXED']),
  },
  {
    id: 'reduceFlashing', group: X, label: 'REDUCE FLASHING', help: 'NO WHITE FLASHES OR SPARKLING SPACE.',
    ...offOn('reduceFlashing'),
  },
  {
    id: 'highContrast', group: X, label: 'HIGH CONTRAST', help: 'ONE BRIGHT SCHEME FOR EVERY CAVE.',
    ...offOn('highContrast'),
  },
  {
    id: 'controls', group: C, label: 'KEYS AND BUTTONS', help: 'REBIND KEYS; SEE THE PAD AND TOUCH.',
    value: () => ({ kind: 'link' }),
  },
  {
    id: 'touch', group: C, label: 'TOUCH CONTROLS', help: 'ON-SCREEN PAD: AUTOMATIC, ON OR OFF.',
    ...choice('touch', ['auto', 'on', 'off'], ['AUTO', 'ON', 'OFF']),
  },
];

/** A line of the list: a group heading or a setting. */
export type OptionLine =
  | { readonly kind: 'group'; readonly label: string }
  | { readonly kind: 'row'; readonly row: number };

/** The rows with a heading before each group. */
export function optionLines(rows: readonly OptionRow[] = OPTION_ROWS): OptionLine[] {
  const lines: OptionLine[] = [];
  rows.forEach((row, i) => {
    if (rows[i - 1]?.group !== row.group) lines.push({ kind: 'group', label: row.group });
    lines.push({ kind: 'row', row: i });
  });
  return lines;
}

/** The line index of row `row`. */
export function lineOfRow(lines: readonly OptionLine[], row: number): number {
  return lines.findIndex((l) => l.kind === 'row' && l.row === row);
}
